import {
	deleteSystemConnection,
	getSystemConnectionById,
	resetSystemConnectionStore,
	resetSystemConnectionTestStatus,
	updateSystemConnection,
} from '@Actions';
import { NewSystemConnectionForm } from '@Components';
import { TestCard } from '@Components/TestCard';
import { useNavigateTo, useYupSchemaValidation } from '@Hooks';
import {
	Features,
	InfoCard,
	WarningDialog,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { BodyGridStyled } from '@Pages/shared/Pages.styles';
import { SystemConnectionSchema } from '@Pages/shared/SystemConnectionSchema';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { MAIN_ROUTES } from '@Types/routes';
import { SystemConnection } from '@Types/systemConnections.types';
import { addRouteNamespace } from '@Utils/actions';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import ControlsButtons from './ControlsButtons';
import PageWrapperStyled, {
	StyledControlsWrapper,
} from './EditSystemConnection.styles';

import { createConnectionString } from '../../hooks/useConnectionString';
import { FLAGS } from '@Types';
import { initialFormData } from './utils';

const EditSystemConnection = () => {
	const { t } = useGlobalization();
	const isCredentialsOptional = !!useIsFeatureEnabled(
		FLAGS.DONT_REQUIRE_CREDENTIALS as keyof Features,
	);
	const isOledbEnabled = !!useIsFeatureEnabled(FLAGS.INFO_360_ADMIN_OLEDB);

	useNavigateTo();
	const dispatch = useDispatch();
	const history = useHistory();
	const params = useParams<{ connectionId: string }>();

	const {
		systemConnections: {
			initialized,
			isUpdating,
			list: connections,
			status: asyncStatus,
			testSystemConnectionStatus,
			isCurrentlyDeleting,
		},
	} = useSelector(getValue);

	const [formData, setFormData] = React.useState<Record<string, string>>({});
	const [formErrors, setFormErrors] = React.useState<Record<string, string>>(
		{},
	);
	const [validate] = useYupSchemaValidation<Record<string, string>>();
	const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
	const [userEnteredUsername, setUserEnteredUsername] = React.useState(false);
	const [userEnteredPassword, setUserEnteredPassword] = React.useState(false);

	const testCredentials = React.useMemo(() => {
		if (isCredentialsOptional) {
			return {
				...(userEnteredUsername && {
					username: formData?.username || '',
				}),
				...(userEnteredPassword && {
					password: formData?.password || '',
				}),
				connectionString: formData?.connectionString || '',
				type: formData?.connectionType || '',
				systemConnectionId: params.connectionId,
			};
		} else {
			return {
				username: formData?.username || '',
				password: formData?.password || '',
				connectionString: formData?.connectionString || '',
				type: formData?.connectionType || '',
			};
		}
	}, [formData]);

	const testCredentialsOPCUA = React.useMemo(() => {
		if (isCredentialsOptional) {
			return {
				...(userEnteredUsername && {
					username: formData?.username || '',
				}),
				...(userEnteredPassword && {
					password: formData?.password || '',
				}),
				opcuaUrl: formData.opcuaUrl,
				endpointMustExist: formData.endpointMustExist,
				securityMode: formData.securityMode,
				securityPolicy: formData.securityPolicy,
				sessionName:
					formData.sessionName ||
					`adsk-edge-client-test-${Date.now()}`,
				systemConnectionId: params.connectionId,
			};
		} else {
			return {
				username: formData.username,
				password: formData.password,
				opcuaUrl: formData.opcuaUrl,
				endpointMustExist: formData.endpointMustExist,
				securityMode: formData.securityMode,
				securityPolicy: formData.securityPolicy,
				sessionName:
					formData.sessionName ||
					`adsk-edge-client-test-${Date.now()}`,
			};
		}
	}, [formData]);

	// These are the fields that the user can edit without requiring the username & password to be entered
	// Any other fields will require a username and password to be entered
	const nonRestrictedFields: string[] = [
		'password',
		'caseSensitiveSensorId',
		'databaseTimezone',
		'dateTimeFormat',
		'sampleDurationSeconds',
		'queryLagMinutes',
		'queryGroupSize',
		'simulationSubtractionDays',
		'quoteFormat',
		'timestampConversion',
		'tagFormat',
	];

	// remove the placeholder if set for the username field
	function clearUsernamePlaceholder() {
		if (!userEnteredUsername) {
			setFormData(currentFormData => ({
				...currentFormData,
				username: '',
			}));
			setUserEnteredUsername(true);
		}
	}

	// remove the placeholder if set for the password field
	function clearPasswordPlaceholder() {
		if (!userEnteredPassword) {
			setFormData(currentFormData => ({
				...currentFormData,
				password: '',
			}));
			setUserEnteredPassword(true);
		}
	}

	// return true if any key in the obect is restricted (i.e. not in the nonRestrictedFields list)
	function containsRestrictedFields(object: Record<string, string>) {
		return Object.keys(object).find(
			key => !nonRestrictedFields.includes(key),
		);
	}

	function updateFormData(
		newFormData: Record<string, string>,
		changeByUser?: boolean,
	): void {
		// skip events that just have undefined fields
		if (Object.keys(newFormData).every(x => x == 'undefined')) {
			return;
		}

		// feature flag AND a change made by user action
		if (
			isCredentialsOptional &&
			changeByUser &&
			Object.keys(newFormData).find(x => x !== 'undefined')
		) {
			if (newFormData.username) {
				setUserEnteredUsername(true);
				clearPasswordPlaceholder();
			}
			if (newFormData.password) {
				setUserEnteredPassword(true);
			}
			// If any restricted fields are present, clear the username and password fields
			if (containsRestrictedFields(newFormData)) {
				clearUsernamePlaceholder();
				clearPasswordPlaceholder();
			}
		}
		if (changeByUser) {
			if (
				'serverAddress' in newFormData ||
				'port' in newFormData ||
				'connectionType' in newFormData ||
				'database' in newFormData ||
				'serverType' in newFormData
			) {
				setFormData(currentFormData => ({
					...currentFormData,
					connectionString: createConnectionString(
						{
							...formData,
							...newFormData,
						} as unknown as SystemConnection,
						isOledbEnabled,
					),
				}));
			}
		}
		setFormData((currentFormData: Record<string, string>) => ({
			...currentFormData,
			...newFormData,
		}));
	}

	function validateFormData(_formData: Record<string, string>) {
		const validationResult = validate(_formData, SystemConnectionSchema(t));

		if (validationResult.hasErrors) {
			setFormErrors(validationResult.errors as Record<string, string>);
			return false;
		}

		setFormErrors({});
		return true;
	}

	// remove placeholder username and password from the form data so the request will be submitted without these fields
	function removePlaceholderText(formData: Record<string, string>) {
		if (isCredentialsOptional) {
			if (!userEnteredUsername) {
				delete formData.username;
			}
			if (!userEnteredPassword) {
				delete formData.password;
			}
		}
	}

	function handleSaveBtnClick() {
		const clonedFormData: Record<string, string> = JSON.parse(
			JSON.stringify(formData),
		);
		if (
			clonedFormData.connectionType &&
			clonedFormData.connectionType === 'OPC-UA'
		) {
			delete clonedFormData.caseSensitiveSensorId;
			delete clonedFormData.connectionString;
			delete clonedFormData.dateTimeFormat;
			delete clonedFormData.quoteFormat;
			delete clonedFormData.serverType;
			delete clonedFormData.editMode;
			delete clonedFormData.undefined;

			if (clonedFormData && validateFormData(clonedFormData)) {
				removePlaceholderText(clonedFormData);
				dispatch(
					updateSystemConnection({
						...clonedFormData,
						connectionId: params.connectionId,
					}),
				);
			}
		}

		if (
			clonedFormData.connectionType &&
			(clonedFormData.connectionType === 'ODBC' ||
				clonedFormData.connectionType === 'OLEDB')
		) {
			delete clonedFormData.editMode;
			delete clonedFormData.opcuaUrl;
			delete clonedFormData.resourcePath;
			delete clonedFormData.undefined;
			delete clonedFormData.securityMode;
			delete clonedFormData.securityPolicy;
			delete clonedFormData.endpointMustExist;
			delete clonedFormData.sessionName;
			if (clonedFormData && validateFormData(clonedFormData)) {
				removePlaceholderText(clonedFormData);
				dispatch(
					updateSystemConnection({
						...clonedFormData,
						connectionId: params.connectionId,
					}),
				);
			}
		}
	}

	const handleBackBtnClick = (): void => {
		if (!initialized || isDeleting) return;
		history.replace(addRouteNamespace('system-connections'));
	};

	useEffect(() => {
		if (asyncStatus === 'completed') {
			dispatch(resetSystemConnectionStore());
		}
	}, [asyncStatus]);

	const systemConnection = useMemo(
		() =>
			connections.find(
				({ SystemConnectionId: id }) => params.connectionId === id,
			),
		[params.connectionId, isUpdating],
	);

	useEffect(() => {
		dispatch(resetSystemConnectionTestStatus());
		// Try to find the Connection in the redux store using the connectionId URL param
		if (initialized) {
			const match = systemConnection;
			// Redirect to the admin page if the connectionId doesn't exist
			if (!match) return history.push(MAIN_ROUTES.SystemConnections);

			setFormData(initialFormData(match, isCredentialsOptional));
		} else {
			// Fetch the System Connections if not present in Redux
			dispatch(getSystemConnectionById(params.connectionId));
		}
	}, [initialized, connections, params]);

	const isDeleting = useMemo(
		() => asyncStatus === 'pending' && isCurrentlyDeleting,
		[isCurrentlyDeleting],
	);

	const handleOpenConfirmDialog = () => {
		setConfirmDialogOpen(true);
	};

	const handleCloseConfirmDialog = () => {
		setConfirmDialogOpen(false);
	};

	const handleConfirmDelete = () => {
		dispatch(deleteSystemConnection({ connectionId: params.connectionId }));
		handleCloseConfirmDialog();
	};

	const renderForm = (credentials: Record<string, string>) => {
		if (!formData) return null;
		return (
			<>
				<PageWrapperStyled
					title={t('Edit System Connection')}
					back={{
						action: handleBackBtnClick,
						label: t('All system connections'),
					}}
					secondary={
						<StyledControlsWrapper>
							<ControlsButtons
								disableMultiaction={asyncStatus === 'pending'}
								onClickSaveButton={handleSaveBtnClick}
								onClickDeleteButton={handleOpenConfirmDialog}
							/>
						</StyledControlsWrapper>
					}
					isLoading={!initialized || isDeleting}
					applyPadding
					headerUnderline>
					<BodyGridStyled container>
						<InfoCard fullHeight title={t('Details')}>
							<NewSystemConnectionForm
								updateFormData={updateFormData}
								formData={formData}
								formErrors={formErrors}
							/>
						</InfoCard>
						<InfoCard fullHeight title={t('Status')}>
							<TestCard
								credentials={credentials}
								status={testSystemConnectionStatus}
								buttonText="Test Connection"
								isLoading={asyncStatus === 'pending'}
							/>
						</InfoCard>
					</BodyGridStyled>
				</PageWrapperStyled>

				<WarningDialog
					onCancel={handleCloseConfirmDialog}
					open={confirmDialogOpen}
					cancelText={t('Cancel')}
					confirmText={t('Delete')}
					title={t('Delete system connection {{id}}', {
						id: formData.connectionName,
					})}
					onConfirm={handleConfirmDelete}>
					{t(
						'Are you sure you want to delete the system connection?',
					)}
				</WarningDialog>
			</>
		);
	};

	switch (formData.connectionType) {
		case 'ODBC':
			return renderForm(testCredentials);
		case 'OPC-UA':
			return renderForm(testCredentialsOPCUA);
		default:
			return renderForm(testCredentials);
	}
};

export default EditSystemConnection;
